<template>
  <button
    @click="toggleId"
    :class="{ active: isActive }"
    class="icon-wrapper rounded-full"
    aria-label="Toggle trip planner"
    part="button"
  >
    <svg
      class="heart-icon"
      id="Layer_2"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 18.91 17.45"
    >
      <g id="Layer_1-2">
        <path
          d="M16.24,2.69c-.87-.87-2.03-1.35-3.27-1.35h0c-.99,0-2.07.47-3.04,1.33l-.11.1c-.22.19-.54.19-.76,0l-.11-.1c-.97-.84-2.04-1.3-3.02-1.3h0c-1.15,0-2.24.42-3.09,1.19-.06.05-.12.11-.17.16-.87.87-1.35,2.03-1.35,3.27,0,1.23.48,2.39,1.36,3.27l6.38,6.36c.17.17.3.25.41.25h0c.11,0,.24-.08.41-.25l6.39-6.4c.87-.87,1.35-2.03,1.35-3.27,0-1.23-.48-2.39-1.36-3.27"
          style="fill: #fff"
        />
        <path
          d="M17.17,1.73C16.05.61,14.56,0,12.97,0c-1.24,0-2.43.39-3.43,1.09-.07.05-.15.05-.22,0C8.33.4,7.15.03,5.92.03c-1.58,0-3.07.62-4.19,1.74C.61,2.89,0,4.38,0,5.96c0,1.58.62,3.07,1.74,4.19l7.03,7.02c.38.38.99.38,1.36,0l7.04-7.05c1.12-1.12,1.73-2.61,1.73-4.19,0-1.58-.62-3.07-1.74-4.19M16.52,9.5l-6.39,6.4c-.25.25-.46.36-.68.36h0c-.22,0-.43-.11-.68-.36l-6.38-6.36c-.94-.94-1.47-2.2-1.47-3.54,0-1.34.52-2.59,1.46-3.54.06-.06.12-.12.19-.18.92-.83,2.11-1.29,3.35-1.29h0c1.07,0,2.23.5,3.27,1.4l.11.1c.07.06.18.06.26,0l.11-.1c1.04-.92,2.21-1.42,3.29-1.42h0c1.34,0,2.59.52,3.54,1.46.95.94,1.47,2.2,1.47,3.54,0,1.34-.52,2.59-1.46,3.54"
          style="fill: #fff"
        />
      </g>
    </svg>
    <span v-if="!isActive">Add to trip plan</span>
    <span v-else>Remove from trip plan</span>
  </button>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watchEffect } from "vue";

// Constants
const LOCAL_STORAGE_KEY = "trip_planner_ids";

// Props
const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
});

// Reactive state
const isActive = ref(false);

/**
 * Retrieves the array of IDs from localStorage.
 * If the key doesn't exist, returns an empty array.
 */
const getStoredIds = () => {
  const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
  try {
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error("Failed to parse localStorage data:", error);
    return [];
  }
};

/**
 * Saves the array of IDs to localStorage and dispatches update event
 * @param {Array} ids - The array of IDs to store.
 */
const saveIds = (ids) => {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(ids));
  // Dispatch custom event with new IDs
  window.dispatchEvent(
    new CustomEvent("tripPlannerUpdate", {
      detail: { ids },
    }),
  );
};

/**
 * Toggles the presence of the current id in localStorage.
 * Adds the id if it's not present; removes it if it is.
 */
const toggleId = () => {
  let ids = getStoredIds();

  if (ids.includes(props.id)) {
    // Remove the id
    ids = ids.filter((id) => id !== props.id);
    isActive.value = false;
  } else {
    // Add the id
    ids.push(props.id);
    isActive.value = true;
  }

  saveIds(ids);
};

/**
 * Check if the current ID is in localStorage and update isActive accordingly
 */
const updateActiveState = () => {
  const ids = getStoredIds();
  isActive.value = ids.includes(props.id);
};

// Lifecycle hooks
onMounted(() => {
  updateActiveState();

  // Add event listeners
  window.addEventListener("storage", updateActiveState);
  window.addEventListener("tripPlannerUpdate", updateActiveState);
});

onUnmounted(() => {
  // Remove event listeners
  window.removeEventListener("storage", updateActiveState);
  window.removeEventListener("tripPlannerUpdate", updateActiveState);
});

// Watch for changes to props.id
watchEffect(() => {
  if (props.id) {
    updateActiveState();
  }
});
</script>

<style>
.icon-wrapper {
  position: relative;
  padding: 4px 10px;
  background: transparent;
  margin-left: var(--vwc-spacing-sm);
  display: flex;
  cursor: pointer;
  border-radius: var(--vwc-border-radius-full);
  /* border: 1px solid var(--vwc-color-primary); */
  background: transparent;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 12px;
}

.icon-wrapper.active {
  background: var(--vwc-active-background);
  border-color: var(--vwc-active-background);
  color: var(--vwc-active-text);
}
.icon-wrapper.active svg {
  fill: var(--vwc-active-text);
}

.heart-icon {
  width: 12px;
  padding-right: var(--vwc-spacing-sm);
}

/* Add ::part selector for component customization */
:host::part(button) {
  /* Custom styles for the button part */
}
</style>
